import React, { useRef, useEffect, useMemo } from "react";

import { ReportAnswer, Skill } from "../../../graphql/types";

import {
  getReportAnswersByRole,
  TemplateQuestionImplProps,
  TemplateQuestionPreviewImplProps,
} from "../TemplateQuestion";

import { Badge, Textarea, Radio, RadioGroup, Input } from "../../wombat-ui";
import { TemplateQuestionFragment } from "../../../graphql/fragments";

import useTranslations from "../../../hooks/useTranslations";
import TemplateQuestionTitle from "./utils/TemplateQuestionTitle";
import Answer from "./utils/Answer";

type TemplateQuestionSkillOptions = {
  value: string;
  label: string;
  isGoal?: boolean;
};

type TemplateQuestionSkillValue = {
  selector: string;
  comment: string;
  timeSpent: number;
};

type TemplateQuestionSkill = {
  options: TemplateQuestionSkillOptions[];
  defaultValue: TemplateQuestionSkillValue;
  subject: Skill;
} & TemplateQuestionFragment;

export function isTemplateQuestionSkill(
  q: TemplateQuestionFragment
): q is TemplateQuestionSkill {
  return q.type === "TemplateQuestionSkill" && q.subjectType === "Skill";
}

export function parseQuestion(
  question: TemplateQuestionFragment
): TemplateQuestionSkill {
  return question as TemplateQuestionSkill;
}

type ReportAnswerSkill = {
  value: TemplateQuestionSkillValue;
} & ReportAnswer;

export function parseAnswers(answers: ReportAnswer[]): ReportAnswerSkill[] {
  return answers as ReportAnswerSkill[];
}

type TemplateQuestionSkillProps = TemplateQuestionImplProps;

export const TemplateQuestionSkill: React.FC<TemplateQuestionSkillProps> = React.memo(
  ({
    question: rawQuestion,
    answers: rawAnswers,
    mad,
    register,
    status,
    displayIndex,
    role,
    name,
    needsToBeFilled,
    onReview,
    autoFocus,
  }) => {
    const t = useTranslations();

    // FIXME: should be const question = rawQuestion as TemplateQuestionSkill ?
    const question = useMemo(() => parseQuestion(rawQuestion), [rawQuestion]);
    const answers = useMemo(
      () => getReportAnswersByRole(rawAnswers, parseAnswers, mad),
      [rawAnswers]
    );

    const firstRadioRef = useRef(null);
    useEffect(() => {
      if (autoFocus) firstRadioRef.current?.focus();
    }, [firstRadioRef.current, autoFocus]);

    return (
      <div>
        <RadioGroup
          display="row"
          title={
            <TemplateQuestionTitle
              title={question.subject.name}
              description={question.description}
              displayIndex={displayIndex}
              referents={question.referents}
            />
          }
          description={t("reports.skillQuestion")}
        >
          {question.options.map((opt, index) => {
            return (
              <Radio
                defaultChecked={
                  answers?.[role === "talent" ? "talent" : "referent"]?.[0]
                    ?.value?.selector === opt.value
                }
                className={
                  opt?.isGoal ? "text-warning-500 bg-warning-100" : undefined
                }
                key={`${opt.value}`}
                label={opt.label}
                help={
                  opt?.isGoal
                    ? (t("templates.targetLevel") as string)
                    : undefined
                }
                id={`${question.subject.name}_${opt.value}`}
                value={opt.value}
                disabled={!needsToBeFilled}
                variant="boxed"
                name={`${name}.value.selector`}
                ref={(ref) => {
                  if (index === 0) firstRadioRef.current = ref;
                  register(ref);
                }}
              />
            );
          })}
        </RadioGroup>
        {question.timeTracked && (
          <Input
            label={
              !question.expectedTimeValue
                ? (t("reports.timeSpentInHour") as string)
                : `${t("reports.timeSpentInHour") as string} - ${t(
                    `reports.expectedTime.${question.expectedTimeUnit}`,
                    {
                      count: question.expectedTimeValue,
                    }
                  )}`
            }
            type="number"
            size="small"
            name={`${name}.timeSpent`}
            disabled={!needsToBeFilled}
            ref={register({ valueAsNumber: true })}
            id={`${question.id}-timeTracked`}
            defaultValue={answers?.talent?.[0]?.timeSpent || undefined}
          />
        )}
        {needsToBeFilled && (
          <div className="max-w-2xl pt-4">
            <Textarea
              defaultValue={
                answers?.[role === "talent" ? "talent" : "referent"]?.[0]?.value
                  .comment
              }
              label={t("reports.comments") as string}
              id={question.id}
              rows={2}
              help={t("reports.commentsHelp") as string}
              ref={register()}
              name={`${name}.value.comment`}
            />
          </div>
        )}
        {(status === "to_be_filled_evaluator" || onReview) && (
          <div className="px-4 py-3 my-2">
            <h4 className="text-sm font-medium leading-5 text-dark-100">
              {t("reports.talentAnswer")}
            </h4>
            <div className="grid grid-cols-1 gap-3 pt-3">
              {answers.talent.map((a) => (
                <Answer
                  answer={a}
                  answerAs={
                    <Badge>
                      {
                        question.options.find(
                          (o) => o.value === a.value.selector
                        )?.label
                      }
                    </Badge>
                  }
                  withComment
                  comment={a.value.comment}
                  key={a.id}
                />
              ))}
            </div>
          </div>
        )}
        {answers.referent.length > 0 && (
          <div className="px-4 py-3 my-2">
            <h4 className="text-sm font-medium leading-5 text-dark-100">
              {t("reports.evaluatorsAnswers", {
                count: answers.referent.length,
              })}
            </h4>
            <div className="grid grid-cols-1 gap-3 pt-3">
              {answers.referent.map((a) => (
                <Answer
                  answer={a}
                  answerAs={
                    <Badge>
                      {
                        question.options.find(
                          (o) => o.value === a.value.selector
                        )?.label
                      }
                    </Badge>
                  }
                  withComment
                  comment={a.value.comment}
                  key={a.id}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
);

type TemplateQuestionSkillPreviewProps = TemplateQuestionPreviewImplProps;

export const TemplateQuestionSkillPreview: React.FC<TemplateQuestionSkillPreviewProps> = React.memo(
  ({ question: rawQuestion, displayIndex }) => {
    const t = useTranslations();

    const question = useMemo(() => parseQuestion(rawQuestion), [rawQuestion]);

    return (
      <div>
        <RadioGroup
          display="row"
          title={
            <TemplateQuestionTitle
              title={question.subject.name}
              displayIndex={displayIndex}
            />
          }
          description={t("reports.skillQuestion")}
        >
          {question.options.map((opt) => (
            <Radio
              key={`${opt.value}`}
              name={question.id}
              label={opt.label}
              id={`${question.subject.name}_${opt.value}`}
              value={opt.value}
              disabled
              variant="boxed"
            />
          ))}
        </RadioGroup>
      </div>
    );
  }
);
